import React, { useState, useEffect, useRef } from "react";
import classes from "./UploadLoanExcelModal.module.css"; 
import { X, Download, Upload, CheckCircle } from "lucide-react";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import api from "../../Components/API/Api";

// ✅ Added onUploadSuccess prop here
const UploadLoanExcelModal = ({ isOpen, onClose, companyId, onUploadSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState(null);
  
  // Form Fields
  const [loanProduct, setLoanProduct] = useState("");
  const [date, setDate] = useState("");
  const [currency, setCurrency] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [bankAccount, setBankAccount] = useState(""); // debit_account

  // Dropdown Data & Loading States
  const [loanProducts, setLoanProducts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const fileInputRef = useRef(null);

  // Reset & Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setFile(null);
      setLoanProduct("");
      setDate("");
      setCurrency("");
      setPaymentMode("");
      setBankAccount("");
      fetchAllDropdownData();
    }
  }, [isOpen]);

  // Fetch all dropdowns in parallel
  const fetchAllDropdownData = async () => {
    if (!companyId) return;
    setLoadingDropdowns(true);
    try {
      // 1. Fetch Loan Products
      const loanRes = await api.get(`/v1/loan_account?company_id=${companyId}`);
      const products = Array.isArray(loanRes.data) ? loanRes.data : loanRes.data?.data || [];
      setLoanProducts(products);

      // 2. Fetch Currencies
      const currencyRes = await api.get('/v1/fetch-all-currencies');
      const currenciesData = currencyRes.data?.status ? (currencyRes.data.data || []) : [];
      setCurrencies(currenciesData);

      // 3. Fetch Payment Modes
      const paymentRes = await api.get('/v1/income/get-payment-method');
      const paymentData = Array.isArray(paymentRes.data?.data) ? paymentRes.data.data : [];
      setPaymentModes(paymentData);

      // 4. Fetch Bank Accounts (for debit_account)
      const bankRes = await api.get(`/v1/bank-accounts?company_id=${companyId}`);
      const bankData = Array.isArray(bankRes.data) ? bankRes.data : bankRes.data?.data || [];
      setBankAccounts(bankData);

    } catch (error) {
      console.error("Error fetching dropdowns", error);
      setLoanProducts([]);
      setCurrencies([]);
      setPaymentModes([]);
      setBankAccounts([]);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get('/download_loan_repayment_template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'loan_repayments_template.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setCurrentStep(2);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: error.response?.data?.message || 'Could not download the template.'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleSubmit = async () => {
    if (!file) {
      Swal.fire({ icon: 'error', title: 'Missing File', text: 'Please upload the completed Excel file.' });
      return;
    }
    if (!loanProduct) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Loan Product.' });
      return;
    }
    if (!date) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Date.' });
      return;
    }
    if (!currency) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Currency.' });
      return;
    }
    if (!paymentMode) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Mode of Payment.' });
      return;
    }
    if (!bankAccount) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Bank Account.' });
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      
      formData.append('company_id', companyId);
      formData.append('file', file);
      formData.append('loan_name', loanProduct); 
      formData.append('currency', currency);
      formData.append('payment_mode', paymentMode);
      formData.append('debit_account', bankAccount);
      formData.append('transaction_date', date);

      const res = await api.post('/upload_loan_repayment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.status === 200 || res.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Upload Successful',
          text: res.data?.message || 'Loan repayments uploaded successfully.'
        });

        // ✅ 1. Refresh the parent table
        if (onUploadSuccess) {
          onUploadSuccess(); 
        }
        
        // ✅ 2. Close the modal
        onClose(); 
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: error.response?.data?.message || 'Failed to upload the file.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div className={classes.customModal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.modalContent}>
          <div className={classes.modalHeader}>
            <div>
              <h5 className={classes.modalTitle}>Upload Loan Repayments</h5>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>
                Three steps to upload your members' loan repayments
              </p>
            </div>
            <button type="button" className={classes.modalCloseBtn} onClick={onClose}>
              <X size={28} />
            </button>
          </div>

          <div className={classes.modalBody} style={{ paddingTop: '10px' }}>
            {/* --- STEP 1 --- */}
            {currentStep === 1 && (
              <>
                <div className={classes.dashedBox}>
                  <div className={classes.iconGreenBox}><Download size={24} /></div>
                  <h4 style={{ margin: '10px 0 5px', fontSize: '16px', fontWeight: 600 }}>loan_repayments.xlsx</h4>
                  <p style={{ fontSize: '13px', color: '#4b5563', maxWidth: '400px', margin: '10px auto 0', lineHeight: 1.5 }}>
                    Download our ready-made template, fill in your data offline at your own pace, then re-upload.
                  </p>
                </div>
                <div className={classes.stepsList}>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Click Download Template</div>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Open the file and fill in your members' loan repayment details</div>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Click Upload document and upload the completed file</div>
                </div>
                <div className={classes.modalFooter} style={{ borderTop: '1px solid #e5e7eb', marginTop: '20px', paddingTop: '16px' }}>
                  <div className={classes.modalBtnRow} style={{ width: '100%', justifyContent: 'space-between' }}>
                    <button type="button" className={classes.btnUploadOutline} onClick={() => setCurrentStep(2)}>
                      <Upload size={18} style={{ marginRight: '6px' }} /> Upload Document
                    </button>
                    <button type="button" className={classes.btnUploadSolid} onClick={handleDownloadTemplate} disabled={isDownloading}>
                      {isDownloading ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                           <Spinner animation="border" size="sm" /> Downloading...
                        </span>
                      ) : (
                        <><Download size={18} style={{ marginRight: '6px' }} /> Download Template</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* --- STEP 2 --- */}
            {currentStep === 2 && (
              <>
                <div className={classes.successHeader}>
                  <CheckCircle size={22} className={classes.greenIcon} fill="#16A34A" color="white" />
                  <span>Template downloaded - now upload your completed file below</span>
                </div>
                <div className={classes.uploadDashedBox} onClick={() => fileInputRef.current?.click()} onDrop={handleDrop} onDragOver={handleDragOver}>
                  <div className={classes.uploadIconBox}><Upload size={24} /></div>
                  <h4 style={{ margin: '0', fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>Click to upload your completed file</h4>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>or drag and drop .xlsx, .xls accepted</p>
                  <input type="file" accept=".xlsx,.xls" hidden ref={fileInputRef} onChange={handleFileSelect} />
                  {file && <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: 500, color: '#16A34A' }}>✓ {file.name}</div>}
                </div>

                {/* === FORM FIELDS === */}
                <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {/* Row 1: Loan Product & Date */}
                  <div className={classes.modalRow3}>
                    <div className={classes.modalCol}>
                      <label>Loan Product</label>
                      <select className={classes.modalInput} value={loanProduct} onChange={(e) => setLoanProduct(e.target.value)}>
                        <option value="">Select Product</option>
                        {loadingDropdowns ? (
                          <option>Loading...</option>
                        ) : loanProducts.map(p => (
                          <option key={p.id} value={p.id}>{p.loan_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className={classes.modalCol}>
                      <label>Date</label>
                      <input type="date" className={classes.modalInput} value={date} onChange={(e) => setDate(e.target.value)} />
                    </div>
                  </div>

                  {/* Row 2: Currency & Mode of Payment */}
                  <div className={classes.modalRow3}>
                    <div className={classes.modalCol}>
                      <label>Currency</label>
                      <select className={classes.modalInput} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                        <option value="">Select Currency</option>
                        {loadingDropdowns ? (
                          <option>Loading...</option>
                        ) : currencies.map(c => (
                          <option key={c.id} value={c.id}>{c.code || c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className={classes.modalCol}>
                      <label>Mode of Payment</label>
                      <select className={classes.modalInput} value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                        <option value="">Select Payment Mode</option>
                        {loadingDropdowns ? (
                          <option>Loading...</option>
                        ) : paymentModes.map(p => (
                          <option key={p.id} value={p.id}>{p.name || p.method_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 3: Bank Account (Required by API as debit_account) */}
                  <div className={classes.modalRow1} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label>Bank Account (Debit Account)</label>
                    <select className={classes.modalInput} value={bankAccount} onChange={(e) => setBankAccount(e.target.value)}>
                      <option value="">Select Bank Account</option>
                      {loadingDropdowns ? (
                        <option>Loading...</option>
                      ) : bankAccounts.map(b => (
                        <option key={b.id} value={b.id}>{b.gl_name || b.name || b.account_name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={classes.modalFooter} style={{ borderTop: '1px solid #e5e7eb', marginTop: '20px', paddingTop: '16px' }}>
                  <div className={classes.modalBtnRow} style={{ width: '100%', justifyContent: 'space-between' }}>
                    <button type="button" className={classes.btnUploadOutline} onClick={onClose}>Cancel</button>
                    <button type="button" className={classes.btnUploadSolid} onClick={handleSubmit} disabled={isSaving}>
                      {isSaving ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><Spinner animation="border" size="sm" /> Saving...</span>
                      ) : ( "Save" )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadLoanExcelModal;